"use client";

import { LoadingBox } from "@/components/ui/LoadingBox";
import { Button } from "@/components/ui/button";
import { useUser } from "@/lib/auth/session";
import type { BlogPostCommentWithReplies } from "@/lib/supabase/types";
import { cn, scrollToElement } from "@/lib/utils";
import { FlameIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { type SortOption, SortSelector } from "../sort-selector";
import { CommentForm } from "./comment-form";
import { CommentPost } from "./comment-post";

export type CommentTreeNode = BlogPostCommentWithReplies & {
	replies: Array<CommentTreeNode>;
};

interface CommentsBoxProps {
	comments: Array<BlogPostCommentWithReplies>;
	postId: string;
	className?: string;
}

export default function CommentsBox({
	comments,
	postId,
	className,
}: CommentsBoxProps) {
	const { user, isUserLoading, error } = useUser();

	const [commentTree, setCommentTree] = useState(buildCommentTree(comments));
	const [sorting, setSorting] = useState<SortOption>();

	// Handle new comments added
	const handleCommentAdded = (newComment?: CommentTreeNode) => {
		if (!newComment) return;
		setCommentTree([...commentTree, newComment]);
	};

	// Handle replies added to comments
	const handleReplyAdded = (
		parentCommentId: string,
		reply: CommentTreeNode,
	) => {
		const updateCommentTree = (
			comments: CommentTreeNode[],
		): CommentTreeNode[] => {
			return comments.map((comment) => {
				if (comment.id === parentCommentId) {
					return {
						...comment,
						replies: [...comment.replies, reply],
					};
				}
				// Recursively check nested replies
				if (comment.replies.length > 0) {
					return {
						...comment,
						replies: updateCommentTree(comment.replies),
					};
				}
				return comment;
			});
		};

		setCommentTree(updateCommentTree(commentTree));
	};

	useEffect(() => {
		if (!sorting) return;
		const ct = buildCommentTree(comments);
		const st = sortComment(ct, sorting);
		setCommentTree(st);
	}, [comments, sorting]);

	if (isUserLoading) {
		return <LoadingBox message="Loading comments" />;
	}
	if (error) {
		console.error(error);
	}

	return (
		<section
			id="comments"
			className={cn("my-8 lg:my-16 antialiased", className)}
		>
			<div className="mx-auto">
				<div className="flex justify-between items-center mb-6">
					<h2 className="text-lg lg:text-2xl font-bold text-primary">
						Discussion {comments?.length > 0 ? `(${comments?.length})` : ""}
					</h2>
					<SortSelector onChange={setSorting} />
				</div>

				<CommentForm
					currentUserId={user?.id || ""}
					postId={postId}
					onSubmit={handleCommentAdded}
				/>

				<div className="flex flex-col gap-4 divide-y-4 divide-primary-34/5">
					{commentTree?.map((comment) => (
						<CommentPost
							key={comment.id}
							comment={comment}
							currentUser={user}
							onReplyAdded={handleReplyAdded}
						/>
					))}
				</div>
			</div>
		</section>
	);
}

// Process comments to create a recursive tree structure
const buildCommentTree = (
	comments: Array<BlogPostCommentWithReplies>,
	parentId: string | null = null,
): CommentTreeNode[] => {
	const filteredComments =
		comments?.filter((comment) => comment.parent_comment_id === parentId) || [];
	return filteredComments.map<CommentTreeNode>((comment) => {
		const replies = buildCommentTree(comments, comment.id);
		return {
			...comment,
			replies,
		};
	});
};

const sortComment = (comments: CommentTreeNode[], sorting: SortOption) => {
	const createdAt = (comment: CommentTreeNode) =>
		new Date(comment.created_at as string).getTime();
	switch (sorting.value) {
		case "popular":
			return comments.sort((a, b) => b.replies.length - a.replies.length);
		case "recent":
			return comments.sort((a, b) => createdAt(b) - createdAt(a));
		case "oldest":
			return comments.sort((a, b) => createdAt(a) - createdAt(b));
		default:
			return comments;
	}
};

export function ViewCommentsButton({ count }: { count: number }) {
	return (
		<Button
			type="button"
			className={cn(
				"flex flex-row gap-1 bg-blue-100 hover:bg-blue-600 transition-all",
				"text-blue-600 hover:text-white rounded-full items-center px-2 py-1",
			)}
			onClick={() => scrollToElement("comments")}
		>
			<FlameIcon className="size-5" />
			<span className="text-xs font-semibold">
				{count > 0 ? `${count} ` : ""}Comments
			</span>
		</Button>
	);
}
